import { readCachedGlb, writeCachedGlb } from '@/utils/glbCache'
import { DownloadFailed } from '@/utils/downloadFailed'

export type GlbProgress = { loaded: number; total: number | null }

export async function fetchGlb(
  url: string,
  cacheKey: string,
  onProgress?: (progress: GlbProgress) => void,
): Promise<ArrayBuffer> {
  const cached = await readCachedGlb(cacheKey)
  if (cached) return cached

  const response = await fetch(url)
  if (!response.ok) throw new DownloadFailed(response.status, response.statusText)

  const buffer = await readWithProgress(response, onProgress)
  await writeCachedGlb(cacheKey, buffer)
  return buffer
}
async function readWithProgress(
  response: Response,
  onProgress?: (progress: GlbProgress) => void,
): Promise<ArrayBuffer> {
  const header = Number(response.headers.get('content-length'))
  const total = Number.isFinite(header) && header > 0 ? header : null

  if (!response.body) {
    const buffer = await response.arrayBuffer()
    onProgress?.({ loaded: buffer.byteLength, total })
    return buffer
  }

  const reader = response.body.getReader()
  const preallocated = total !== null ? new Uint8Array(total) : null
  const chunks: Uint8Array[] = []
  let loaded = 0

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value) continue
    if (preallocated && loaded + value.length <= preallocated.length) {
      preallocated.set(value, loaded)
    } else {
      chunks.push(value)
    }
    loaded += value.length
    onProgress?.({ loaded, total })
  }

  if (preallocated && chunks.length === 0 && loaded === preallocated.length) {
    return preallocated.buffer
  }
  return concat(chunks, preallocated, loaded)
}

function concat(
  chunks: Uint8Array[],
  preallocated: Uint8Array | null,
  loaded: number,
): ArrayBuffer {
  const merged = new Uint8Array(loaded)
  let offset = 0
  if (preallocated) {
    offset = Math.min(loaded, preallocated.length)
    merged.set(preallocated.subarray(0, offset), 0)
  }
  for (const chunk of chunks) {
    merged.set(chunk, offset)
    offset += chunk.length
  }
  return merged.buffer
}
